import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import { useForm } from "../../../hooks/useForm";
import useDialog from "../../../hooks/useDialog";
import EnclosuresByItemDialog from "../../dialog/EnclosuresByItemDialog/EnclosuresByItemDialog";
import { useSnackbarAlert } from "../../../context/snackbarAlert";
import { getErrorMsg } from "../../../helpers/methods";
import TaskUpperForm from "./TaskUpperForm";
import LocalizationForm from "../../drawer/UniversalDetailsDrawer/UniversalDetailsDrawerForms/LocalizationForm";
import ActionIconsForm from "../../drawer/UniversalDetailsDrawer/UniversalDetailsDrawerForms/ActionIconsForm";
import TaskLeftForm from "./TaskLeftForm";

const TaskDetailsForm = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const snackbarAlert = useSnackbarAlert();

  const {
    formValue,
    setFormValue,
    onChangeWithCallback,
    onChangeAutocompleteFieldWithObjectOptionsAndCallback,
    onChangeDateWithCallback,
  } = useForm(props.itemData);

  const [
    openEnclosuresDialog,
    onOpenEnclosuresDialog,
    onCloseEnclosuresDialog,
  ] = useDialog();

  const handleChangeFieldValue = (name, value) => {
    setIsLoading(true);
    const oldValue = formValue[name];
    props
      .onUpdateDataPromise(formValue.id, { [name]: value })
      .then(() => {
        props.onRefetchData();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(getErrorMsg(error.data));
        setFormValue((prev) => ({ ...prev, [name]: oldValue }));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChangeLocations = (locationData, onCloseDialog) => {
    const oldLocationData = formValue.locations;
    setIsLoading(true);
    props
      .onUpdateDataPromise(formValue.id, { objects: locationData })
      .then((data) => {
        setFormValue((prev) => ({ ...prev, locations: data.locations }));
        onCloseDialog();
        props.onRefetchData();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(getErrorMsg(error.data));
        setFormValue((prev) => ({ ...prev, locations: oldLocationData }));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChangeStatus = (statusData, onCloseDialog) => {
    const oldStatus = formValue.ticket_status;
    const oldSubStatus = formValue.ticket_substatus;
    setIsLoading(true);
    props
      .onUpdateDataPromise(formValue.id, { status_data: statusData })
      .then((data) => {
        setFormValue((prev) => ({
          ...prev,
          ticket_status: data.ticket_status,
          ticket_substatus: data.ticket_substatus,
        }));
        onCloseDialog();
        props.onRefetchData();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(getErrorMsg(error.data));
        setFormValue((prev) => ({
          ...prev,
          ticket_status: oldStatus,
          ticket_substatus: oldSubStatus,
        }));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onOpenEnclosuresDialogLocal = () => {
    onOpenEnclosuresDialog();
  };

  const onCloseEnclosuresDialogLocal = (existsChanges) => {
    if (existsChanges) {
      props.onRefetchData();
    }
    onCloseEnclosuresDialog();
  };


  return (
    <Grid container rowSpacing={1} columnSpacing={1}>
      <Grid item xs={12}>
        <TaskUpperForm
          readOnly={props.readOnly}
          formValue={formValue}
          filteringData={
            formValue?.is_closed === true || props.readOnly
              ? props.filteringData
              : props.selectingData
          }
          onChange={onChangeWithCallback(handleChangeFieldValue)}
          onChangeAutocompleteFieldWithObjectOptions={onChangeAutocompleteFieldWithObjectOptionsAndCallback(
            handleChangeFieldValue
          )}
        />
      </Grid>
     <Grid item xs={12} md={7}>
        <TaskLeftForm
          readOnly={props.readOnly}
          isTicketClaim={props.isTicketClaim}
          itemType={props.itemType}
          formValue={formValue}
          filteringData={
            formValue?.is_closed === true || props.readOnly
              ? props.filteringData
              : props.selectingData
          }
          onChange={onChangeWithCallback(handleChangeFieldValue)}
          onChangeDate={onChangeDateWithCallback(handleChangeFieldValue)}
          onChangeAutocompleteFieldWithObjectOptions={onChangeAutocompleteFieldWithObjectOptionsAndCallback(
            handleChangeFieldValue
          )}
          isOM={props.isOM}
          isUserFromExternalTenant={props.isUserFromExternalTenant}
        />
      </Grid> 
      <Grid item xs={12} md={5}>
        <LocalizationForm
          inBox={true}
          itemForChangeLocalizationId={formValue.id}
          isAllowedToChangeLocalization={
            !props.readOnly 
          }
          localizationData={formValue.locations}
          isMultipleLocalization={formValue.is_multiple_locations}
          titleVariant={"h6"}
          onChangeLocalization={handleChangeLocations}
          boxHeight={
            props.itemType === "ticket"
              ? props.isOM
                ? "320px"
                : "330px"
              : "425px"
          }
        />
        <ActionIconsForm
          itemId={formValue.order_id}
          itemData={formValue}
          isItemClosed={props.readOnly}
          isItemFinallyClosed={props.isItemFinallyClosed}
          isItemCancelled={formValue.is_cancel}
          onChangeStatus={handleChangeStatus}
          isAllowedToChangeStatusManualy={ formValue.is_allowed_to_manualy_change_status }
          isLoading={isLoading}
          enclosuresCount={formValue.enclosures_count}
          onClickEnclosures={onOpenEnclosuresDialogLocal}
          availableActionButtons={
            props.hasPermissionToManageTasks
          ? ["enclosures", "change_status", "equipment_readings"]
          : ["enclosures"]}
          itemType={"task"}
          onRefetchData={props.onRefetchData}

        />
      </Grid>
      {openEnclosuresDialog && (
        <EnclosuresByItemDialog
          open={openEnclosuresDialog}
          onClose={onCloseEnclosuresDialogLocal}
          itemId={formValue.id}
          itemType={props.itemType}
        />
      )}
    </Grid>
  );
};

TaskDetailsForm.propTypes = {
  itemType: PropTypes.oneOf(["ticket", "order"]),
  filteringData: PropTypes.object,
  itemData: PropTypes.object,
  onUpdateDataPromise: PropTypes.func,
  onRefetchData: PropTypes.func,
  readOnly: PropTypes.bool,
  isItemFinallyClosed: PropTypes.bool,
  isTicketClaim: PropTypes.bool,
  isOM: PropTypes.bool,
};

TaskDetailsForm.defaultProps = {
  readOnly: false,
};

export default TaskDetailsForm;
